/**
 * Sentry configuration utilities for Simulcast_Auction
 * All configuration is managed in this file without relying on environment files
 */

// ===== CONFIGURATION SECTION =====
// Update these values directly in this file

const SENTRY_CONFIG = {
  // Your Sentry DSN - replace with your actual DSN from Sentry project settings
  dsn: "https://435fe68f6d1dd52a0b300e9e3908f974@o4509810351013888.ingest.us.sentry.io/4510822418481152",

  // Environment detection based on hostname
  environments: {
    localhost: "localhost",
    "127.0.0.1": "localhost",
    dev: "dev",
    qa: "qa",
    uat: "uat",
    regression: "regression",
    demo: "demo",
    staging: "staging",
    production: "production",
  },

  // Traces sample rates by environment
  tracesSampleRates: {
    localhost: 0.1,
    dev: 1.0,
    qa: 0.5,
    regression: 0.5,
    uat: 0.3,
    demo: 0.3,
    staging: 0.2,
    production: 0.1,
  },

  // Replay sample rates by environment
  replaySampleRates: {
    localhost: 0.0,
    dev: 0.1,
    qa: 0.05,
    regression: 0.05,
    uat: 0.02,
    demo: 0.02,
    staging: 0.01,
    production: 0.01,
  },

  // Enable/disable Sentry
  enabled: true,

  // Sensitive fields to scrub from events
  sensitiveFields: [
    "password",
    "token",
    "accessToken",
    "refreshToken",
    "authorization",
    "apiKey",
    "secret",
    "creditCard",
    "ssn",
    "cvv",
  ],

  // Sensitive headers to scrub
  sensitiveHeaders: [
    "authorization",
    "cookie",
    "x-api-key",
    "x-auth-token",
  ],
};

// ===== UTILITY FUNCTIONS =====

/**
 * Detect current environment based on hostname
 * @returns {string} Environment name
 */
export const detectEnvironment = () => {
  const hostname = window.location.hostname.toLowerCase();

  // Check for exact matches
  if (SENTRY_CONFIG.environments[hostname]) {
    return SENTRY_CONFIG.environments[hostname];
  }

  // Check for partial matches
  for (const [key, value] of Object.entries(SENTRY_CONFIG.environments)) {
    if (hostname.includes(key)) {
      return value;
    }
  }

  // Default to production for unknown environments
  return "production";
};

/**
 * Get Sentry DSN
 * @returns {string} Sentry DSN
 */
export const getSentryDSN = () => {
  return SENTRY_CONFIG.dsn;
};

/**
 * Get traces sample rate based on environment
 * @returns {number} Sample rate (0.0 to 1.0)
 */
export const getTracesSampleRate = () => {
  const env = detectEnvironment();
  return SENTRY_CONFIG.tracesSampleRates[env] || 0.1;
};

/**
 * Get replay sample rate based on environment
 * @returns {number} Sample rate (0.0 to 1.0)
 */
export const getReplaySampleRate = () => {
  const env = detectEnvironment();
  return SENTRY_CONFIG.replaySampleRates[env] ?? 0.01;
};

/**
 * Get tenant/environment name
 * @returns {string} Tenant name
 */
export const getTenant = () => {
  return detectEnvironment();
};

/**
 * Check if Sentry is enabled
 * @returns {boolean} True if enabled
 */
export const isSentryEnabled = () => {
  // Only enable Sentry for upper (production) environments
  const lowerEnvs = ["dev", "qa", "reg", "regression", "uat", "training", "development", "localhost"];
  const env = detectEnvironment().toLowerCase();

  return (
    SENTRY_CONFIG.enabled &&
    !!SENTRY_CONFIG.dsn &&
    SENTRY_CONFIG.dsn !== "https://your-sentry-dsn@sentry.io/your-project-id" &&
    !lowerEnvs.includes(env)
  );
};

/**
 * Scrub sensitive data from Sentry events
 * @param {Object} event - Sentry event object
 * @returns {Object} Scrubbed event
 */
export const beforeSend = (event) => {
  // Scrub sensitive data from request data
  if (event.request?.data && typeof event.request.data === "object") {
    SENTRY_CONFIG.sensitiveFields.forEach((field) => {
      if (field in event.request.data) {
        event.request.data[field] = "[Filtered]";
      }
    });
  }

  // Scrub sensitive headers
  if (event.request?.headers && typeof event.request.headers === "object") {
    const headers = event.request.headers;
    SENTRY_CONFIG.sensitiveHeaders.forEach((header) => {
      const lower = header.toLowerCase();
      if (lower in headers) {
        headers[lower] = "[Filtered]";
      }
    });
  }

  return event;
};

/**
 * Get complete Sentry configuration
 * @returns {Object} Sentry configuration object
 */
export const getSentryConfig = () => {
  return {
    dsn: getSentryDSN(),
    environment: detectEnvironment(),
    enabled: isSentryEnabled(),
    tracesSampleRate: getTracesSampleRate(),
    replaySampleRate: getReplaySampleRate(),
    tenant: getTenant(),
  };
};

// Export the raw config for advanced usage
export { SENTRY_CONFIG };
